import { ref, reactive } from "vue";
import { GooglePlace } from "../types";

declare global {
  interface Window {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    google: any;
  }
}

const getComponent = (addressComponents, type) => {
  const comp = addressComponents?.find((c) => c.types.includes(type));
  return comp ? comp.long_name : "";
};

function getCityName(addressComponents, country) {
  if (country !== "CO") {
    // For countries other than Colombia, use the standard locality
    return getComponent(addressComponents, "locality");
  }

  // For Colombia, we need to handle locality and admin_level_2 differently
  // because locality might not always be the city name.
  const locality = getComponent(addressComponents, "locality");
  const adminLevel2 = getComponent(
    addressComponents,
    "administrative_area_level_2"
  );

  // Key insight: If both exist AND they're different names,
  // then locality is likely a sub-division of admin_level_2
  if (locality && adminLevel2 && locality !== adminLevel2) {
    return adminLevel2;
  }

  // Otherwise, prefer locality (standard behavior)
  return locality || adminLevel2 || "";
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useGoogleApiAutocompleteIntegration() {
  const mapsApiKey = ref<string>(
    process.env.VUE_APP_GOOGLE_MAPS_AUTOCOMPLETE_KEY || ""
  );
  const place = ref<GooglePlace | null>(null);
  const isStreetNameAutoCompleteUsed = ref<boolean>(false);
  const isProposedAddressSelected = ref<boolean>(false);
  const isZipCodeAutoCompleteFilled = ref<boolean>(false);
  const highlightFields = ref<boolean>(false);

  const loadGoogleMapsScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (window.google && window.google.maps) {
        resolve();
        return;
      }

      if (!mapsApiKey.value) {
        console.error("Google Maps API key is missing.");
        reject(new Error("Google Maps API key is missing."));
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${mapsApiKey.value}&libraries=places`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        resolve();
      };

      script.onerror = (error) => {
        console.error("Failed to load Google Maps script:", error);
        reject(new Error("Failed to load Google Maps script."));
      };

      document.head.appendChild(script);
    });
  };

  const components = reactive({
    postalCode: "",
    street: "",
    streetNumber: "",
    region: "",
    neighborhood: "",
    city: "",
  });

  const setupAutocomplete = (input, country) => {
    const autocompleteInstance = new google.maps.places.Autocomplete(input, {
      types: ["address"],
      componentRestrictions: { country: country },
    });

    autocompleteInstance.addListener("place_changed", () => {
      place.value = autocompleteInstance.getPlace();

      const addressComponents = place.value?.address_components || [];

      components.postalCode = getComponent(addressComponents, "postal_code");
      components.street = getComponent(addressComponents, "route");
      components.streetNumber = getComponent(
        addressComponents,
        "street_number"
      );
      components.region = getComponent(
        addressComponents,
        "administrative_area_level_1"
      );
      components.neighborhood =
        getComponent(addressComponents, "sublocality_level_1") ||
        getComponent(addressComponents, "neighborhood");
      components.city = getCityName(addressComponents, country);

      isStreetNameAutoCompleteUsed.value = true;
      isProposedAddressSelected.value = true;
      highlightFields.value = true;
    });
  };

  return {
    mapsApiKey,
    loadGoogleMapsScript,
    setupAutocomplete,
    components,
    isStreetNameAutoCompleteUsed,
    isProposedAddressSelected,
    isZipCodeAutoCompleteFilled,
    highlightFields,
  };
}
